import camelcaseKeys from 'camelcase-keys';
import { AlertRuleEvent, DatabaseEvent } from '@/types/event';

export enum WSMessageType {
    ALERT = 'alert',
    DATABASE_EVENT = 'database_event',
    PONG = 'pong',
    PING = 'ping',
}

interface WSMessage {
    type: WSMessageType;
    [key: string]: unknown;
}

type ConnectionState = 'connected' | 'disconnected' | 'reconnecting';
type ConnectionListener = (state: ConnectionState) => void;
type MessageListener<T = unknown> = (data: T) => void;

interface WebSocketConfig {
    url: string;
    reconnect: {
        initialDelay: number;
        maxDelay: number;
        backoffMultiplier: number;
    };
    heartbeat: {
        interval: number;
    };
    beforeReconnect?: () => Promise<unknown>;
}

export class WebSocketService {
    private ws: WebSocket | null = null;
    private state: ConnectionState = 'disconnected';
    private reconnectAttempts = 0;
    private reconnectTimer: NodeJS.Timeout | null = null;
    private pingTimer: NodeJS.Timeout | null = null;
    private shouldReconnect = false;
    private visibilityListener: (() => void) | null = null;

    private connectionListeners = new Set<ConnectionListener>();
    private messageListeners = new Map<
        WSMessageType,
        Set<MessageListener<unknown>>
    >();

    constructor(private config: WebSocketConfig) {}

    connect(): void {
        if (this.state === 'connected') return;

        this.shouldReconnect = true;
        this.setState('reconnecting');
        this.cleanup();

        if (document.hidden) {
            this.deferConnectUntilVisible();
            return;
        }

        try {
            this.ws = new WebSocket(this.config.url);
            this.setupHandlers();
        } catch (error) {
            console.error('[WS] Connection failed:', error);
            this.scheduleReconnect();
        }
    }

    disconnect(): void {
        this.shouldReconnect = false;
        this.cleanup();
        this.setState('disconnected');
    }

    getState(): ConnectionState {
        return this.state;
    }

    isConnected(): boolean {
        return this.state === 'connected';
    }

    onConnectionChange(listener: ConnectionListener): () => void {
        this.connectionListeners.add(listener);
        listener(this.state);
        return () => this.connectionListeners.delete(listener);
    }

    on<T = unknown>(
        messageType: WSMessageType,
        listener: MessageListener<T>,
    ): () => void {
        if (!this.messageListeners.has(messageType)) {
            this.messageListeners.set(messageType, new Set());
        }
        this.messageListeners
            .get(messageType)!
            .add(listener as MessageListener<unknown>);

        return () => {
            this.messageListeners
                .get(messageType)
                ?.delete(listener as MessageListener<unknown>);
        };
    }

    send(message: Record<string, unknown>): boolean {
        if (this.state !== 'connected' || !this.ws) {
            console.warn('[WS] Cannot send message: not connected');
            return false;
        }

        try {
            this.ws.send(JSON.stringify(message));
            return true;
        } catch (error) {
            console.error('[WS] Send failed:', error);
            return false;
        }
    }

    private setupHandlers(): void {
        if (!this.ws) return;

        this.ws.onopen = () => {
            this.reconnectAttempts = 0;
            this.setState('connected');
            this.startHeartbeat();
        };

        this.ws.onmessage = (event: MessageEvent) => {
            this.handleMessage(event);
        };

        this.ws.onclose = () => {
            this.stopHeartbeat();
            if (this.shouldReconnect) {
                this.scheduleReconnect();
            } else {
                this.setState('disconnected');
            }
        };

        this.ws.onerror = () => {
            this.ws?.close();
        };
    }

    private handleMessage(event: MessageEvent): void {
        try {
            const message: WSMessage = JSON.parse(event.data);

            if (message.type === WSMessageType.PONG) {
                return;
            }

            if (message.type === WSMessageType.ALERT) {
                this.notifyListeners(
                    WSMessageType.ALERT,
                    this.transformMessage<AlertRuleEvent>(message),
                );
                return;
            }

            if (message.type === WSMessageType.DATABASE_EVENT) {
                this.notifyListeners(
                    WSMessageType.DATABASE_EVENT,
                    this.transformMessage<DatabaseEvent>(message),
                );
                return;
            }
        } catch (error) {
            console.warn('[WS] Failed to parse message:', error);
        }
    }

    private transformMessage<T>(message: WSMessage): T {
        return camelcaseKeys(message as Record<string, unknown>, {
            deep: true,
        }) as unknown as T;
    }

    private setState(state: ConnectionState): void {
        if (this.state === state) return;
        this.state = state;
        this.connectionListeners.forEach((listener) => listener(state));
    }

    private notifyListeners<T>(messageType: WSMessageType, data: T): void {
        const listeners = this.messageListeners.get(messageType);
        if (listeners) {
            listeners.forEach((listener) =>
                (listener as MessageListener<T>)(data),
            );
        }
    }

    private startHeartbeat(): void {
        this.pingTimer = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({ type: WSMessageType.PING }));
            }
        }, this.config.heartbeat.interval);
    }

    private stopHeartbeat(): void {
        if (this.pingTimer) {
            clearInterval(this.pingTimer);
            this.pingTimer = null;
        }
    }

    private scheduleReconnect(): void {
        if (!this.shouldReconnect) return;

        this.setState('reconnecting');
        this.reconnectAttempts++;

        const delay = Math.min(
            this.config.reconnect.initialDelay *
                Math.pow(
                    this.config.reconnect.backoffMultiplier,
                    this.reconnectAttempts - 1,
                ),
            this.config.reconnect.maxDelay,
        );

        this.reconnectTimer = setTimeout(async () => {
            await this.config.beforeReconnect?.().catch(() => undefined);
            if (this.shouldReconnect) this.connect();
        }, delay);
    }

    private deferConnectUntilVisible(): void {
        this.setState('reconnecting');
        if (this.visibilityListener) return;

        this.visibilityListener = () => {
            if (document.hidden) return;
            this.removeVisibilityListener();
            this.connect();
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
    }

    private removeVisibilityListener(): void {
        if (!this.visibilityListener) return;
        document.removeEventListener(
            'visibilitychange',
            this.visibilityListener,
        );
        this.visibilityListener = null;
    }

    private cleanup(): void {
        this.removeVisibilityListener();
        this.stopHeartbeat();

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        if (this.ws) {
            this.ws.onopen = null;
            this.ws.onmessage = null;
            this.ws.onclose = null;
            this.ws.onerror = null;

            if (
                this.ws.readyState === WebSocket.OPEN ||
                this.ws.readyState === WebSocket.CONNECTING
            ) {
                this.ws.close();
            }
            this.ws = null;
        }
    }
}
